/**
 * Reviewable static biomarker→category map — the curated layer between the
 * hand-authored `BIOMARKER_CONFIG` and the `categorizeByRules` engine.
 *
 * WHY THIS EXISTS: categorization used to be *only* a runtime rule engine, so the
 * category of any given LOINC was never written down anywhere reviewable. This map
 * is the checked-in, clinician-auditable listing of `{ code, name, category }` for
 * the quantitative LOINC universe. `getCategoryForLoinc` consults it BEFORE the
 * rules, so a reviewed entry is durable — a later rule change can't silently
 * re-categorize a code someone already signed off on.
 *
 * SOURCE OF TRUTH: the JSON is hand-editable. It is seeded from the rule engine
 * (see `apps/web/scripts/trends/generate-biomarker-category-map.ts`), then corrected
 * by review. Excludes `BIOMARKER_CONFIG` codes (those resolve higher up) and "other"
 * (the rules stay the runtime fallback for anything absent here).
 *
 * LAZY LOAD: the ~250 KB JSON is loaded via `require` inside a getter (not a
 * top-level import) so importing `@vibrant/models` doesn't parse the whole catalog
 * at boot — the parse defers to the first categorization lookup (i.e. when Trends is
 * used). The `name` is review-only; runtime reads code→category through the memoized
 * `categoryByLoinc` Map.
 */

import type { FunctionalSystem } from "./types";

export interface BiomarkerCategoryMapEntry {
  code: string;
  name: string;
  /** A real functional system — never "other" (uncategorized codes are omitted). */
  category: Exclude<FunctionalSystem, "other">;
}

let entries: readonly BiomarkerCategoryMapEntry[] | undefined;
let categoryByLoinc: Map<string, Exclude<FunctionalSystem, "other">> | undefined;

/** The reviewed listing, lazily parsed and memoized on first access. */
export function getBiomarkerCategoryMap(): readonly BiomarkerCategoryMapEntry[] {
  if (!entries) {
    entries = require("./biomarker-category-map.json") as BiomarkerCategoryMapEntry[];
  }
  return entries;
}

/**
 * Reviewed category for a BASE LOINC, or `undefined` when the code isn't in the map
 * (caller falls through to the rule engine). Callers must strip unit-split /
 * source-qualified key suffixes to the base LOINC first, same as the rest of the resolver.
 */
export function getStaticCategoryForLoinc(
  loincCode: string,
): Exclude<FunctionalSystem, "other"> | undefined {
  if (!categoryByLoinc) {
    categoryByLoinc = new Map(getBiomarkerCategoryMap().map((e) => [e.code, e.category]));
  }
  return categoryByLoinc.get(loincCode);
}
